import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatRM, formatApiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Copy, Loader2, TrendingUp, Wallet, Users, Gift, ArrowLeft, Download, Landmark, Save } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

export default function AgentHub() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [summary, setSummary] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bank, setBank] = useState({ bank_holder: "", bank_name_agent: "", bank_number_agent: "" });
  const [savingBank, setSavingBank] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/agent/summary"),
      api.get("/agent/referrals"),
      api.get("/agent/commissions"),
      api.get("/withdrawals"),
    ]).then(([s, r, c, w]) => {
      setSummary(s.data);
      setReferrals(r.data);
      setCommissions(c.data);
      setWithdrawals(w.data);
    }).catch(() => {});
  };

  useEffect(() => {
    if (user && user.role === "agent" && user.agent_status === "approved") load();
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (user) setBank({
      bank_holder: user.bank_holder || "",
      bank_name_agent: user.bank_name_agent || "",
      bank_number_agent: user.bank_number_agent || "",
    });
  }, [user]);

  const saveBank = async (e) => {
    e.preventDefault();
    setSavingBank(true);
    try {
      await api.patch("/auth/profile", bank);
      await refresh();
      toast.success(t("common.saved"));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingBank(false); }
  };

  if (!user) return null;
  if (user.role !== "agent" || user.agent_status !== "approved") {
    return <Navigate to="/dashboard" replace />;
  }
  if (!summary) return <div className="p-16 text-[#4a6e77]">{t("common.loading")}</div>;

  const referralLink = `${window.location.origin}/register?ref=${summary.referral_code}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success(t("common.copied"));
    } catch { toast.error("Copy failed"); }
  };

  const submitWithdraw = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt < 100) { toast.error(t("agentHub.minAmount")); return; }
    if (!user.bank_holder || !user.bank_name_agent || !user.bank_number_agent) {
      toast.error(t("agentHub.bankRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/withdrawals", { amount: amt });
      toast.success(t("agentHub.requestSubmitted"));
      setAmount("");
      await refresh();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200";

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16" data-testid="agent-hub-page">
      <Link to="/dashboard" className="text-sm text-[#4a6e77] hover:text-[#005870] inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> {t("common.back")}
      </Link>
      <h1 className="mt-3 font-serif-cn text-4xl text-[#003645] font-semibold tracking-tight">
        {t("agentHub.title")}
      </h1>

      {/* Stats */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-lg border border-[#e5ebeb] bg-white p-5" data-testid="stat-earned">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#4a6e77]">
            <TrendingUp size={14} /> {t("agentHub.totalEarned")}
          </div>
          <div className="mt-2 text-2xl font-semibold text-[#005870]">{formatRM(summary.total_earned)}</div>
        </div>
        <div className="rounded-lg border border-[#005870]/25 bg-[#005870] text-white p-5" data-testid="stat-withdrawable">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70">
            <Wallet size={14} /> {t("agentHub.withdrawable")}
          </div>
          <div className="mt-2 text-2xl font-semibold">{formatRM(summary.withdrawable)}</div>
        </div>
        <div className="rounded-lg border border-[#e5ebeb] bg-white p-5" data-testid="stat-referrals">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#4a6e77]">
            <Users size={14} /> {t("agentHub.referredCount")}
          </div>
          <div className="mt-2 text-2xl font-semibold text-[#005870]">{summary.referred_count}</div>
        </div>
        <div className="rounded-lg border border-[#e5ebeb] bg-white p-5" data-testid="stat-code">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#4a6e77]">
            <Gift size={14} /> {t("agentHub.referralCode")}
          </div>
          <div className="mt-2 text-xl font-semibold text-[#005870] tracking-wider">{summary.referral_code}</div>
        </div>
      </div>

      {/* Referral link + QR */}
      <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-5 grid md:grid-cols-[1fr,auto] gap-6 items-center" data-testid="referral-link-section">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#4a6e77]">{t("agentHub.referralLink")}</div>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <input readOnly value={referralLink} className={`${inputCls} font-mono text-sm`} data-testid="referral-link-input" />
            <button onClick={copyLink} className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] transition-colors duration-200" data-testid="copy-referral-link">
              <Copy size={14} /> {t("agentHub.copyLink")}
            </button>
          </div>
        </div>
        <div className="flex flex-col items-center gap-2" data-testid="referral-qr-section">
          <div className="p-3 rounded-md bg-white border border-[#e5ebeb]">
            <QRCodeCanvas
              id="jl-referral-qr"
              value={referralLink}
              size={128}
              level="M"
              includeMargin={false}
              fgColor="#003645"
              data-testid="referral-qr"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              const canvas = document.getElementById("jl-referral-qr");
              if (!canvas) return;
              const link = document.createElement("a");
              link.href = canvas.toDataURL("image/png");
              link.download = `jinlin-referral-${summary.referral_code}.png`;
              link.click();
            }}
            className="inline-flex items-center gap-1.5 text-xs text-[#005870] hover:text-[#00868e] font-medium"
            data-testid="download-qr-btn"
          >
            <Download size={12} /> {t("qrDownload")}
          </button>
        </div>
      </div>

      {/* Bank info edit card */}
      <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-5" data-testid="agent-bank-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#00868e] font-semibold">
          <Landmark size={14} /> {t("agentHub.bankInfo")}
        </div>
        <form onSubmit={saveBank} className="mt-4 grid sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("agentHub.bankHolder")}</label>
            <input className={inputCls} value={bank.bank_holder} onChange={(e) => setBank({ ...bank, bank_holder: e.target.value })} data-testid="agent-bank-holder-input" />
          </div>
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("agentHub.bankName")}</label>
            <input className={inputCls} value={bank.bank_name_agent} onChange={(e) => setBank({ ...bank, bank_name_agent: e.target.value })} data-testid="agent-bank-name-input" />
          </div>
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("agentHub.bankNumber")}</label>
            <input className={inputCls} value={bank.bank_number_agent} onChange={(e) => setBank({ ...bank, bank_number_agent: e.target.value })} data-testid="agent-bank-number-input" />
          </div>
          <div className="sm:col-span-3">
            <button type="submit" disabled={savingBank} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="agent-bank-save-btn">
              {savingBank ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t("common.save")}
            </button>
          </div>
        </form>
      </div>

      {/* Monthly + Withdrawal request */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-[#e5ebeb] bg-white p-6">
          <div className="text-xs uppercase tracking-widest text-[#4a6e77]">{t("agentHub.monthly")}</div>
          <ul className="mt-3 divide-y divide-[#e5ebeb]" data-testid="monthly-earnings">
            {Object.entries(summary.monthly || {}).sort((a, b) => b[0].localeCompare(a[0])).map(([k, v]) => (
              <li key={k} className="py-2 flex justify-between text-sm">
                <span className="text-[#4a6e77]">{k}</span>
                <span className="text-[#003645] font-medium">{formatRM(v)}</span>
              </li>
            ))}
            {Object.keys(summary.monthly || {}).length === 0 && <li className="py-2 text-[#4a6e77]">{t("common.empty")}</li>}
          </ul>
        </div>

        <form onSubmit={submitWithdraw} className="rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="withdraw-form">
          <div className="text-xs uppercase tracking-widest text-[#4a6e77]">{t("agentHub.withdraw")}</div>
          <label className="block text-xs text-[#4a6e77] mt-3 mb-1.5">{t("agentHub.amount")}</label>
          <input type="number" min="100" step="0.01" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="withdraw-amount-input" />
          <div className="mt-1 text-xs text-[#4a6e77]">{t("agentHub.minAmount")}</div>
          {(!user.bank_holder || !user.bank_name_agent || !user.bank_number_agent) && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-50 rounded px-3 py-2" data-testid="withdraw-bank-warning">
              {t("agentHub.bankRequired")} —{" "}
              <Link to="/profile" className="underline">{t("nav.profile")}</Link>
            </div>
          )}
          <button type="submit" disabled={submitting} className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-60" data-testid="withdraw-submit-btn">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {t("agentHub.withdraw")}
          </button>
        </form>
      </div>

      {/* Referrals list */}
      <h2 className="mt-10 font-serif-cn text-xl text-[#003645] font-semibold">{t("agentHub.referrals")}</h2>
      <div className="mt-3 rounded-lg border border-[#e5ebeb] bg-white overflow-hidden" data-testid="referrals-list">
        {referrals.length === 0 ? (
          <div className="p-6 text-[#4a6e77]">{t("common.empty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-[#4a6e77]">
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Name</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Role</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Phone</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">{t("agentHub.joinDate")}</th>
            </tr></thead>
            <tbody>
              {referrals.map((r) => (
                <tr key={r.id}>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#003645]">{r.name}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">{r.role}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77] font-mono text-xs">{r.phone_masked}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77] text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Commissions */}
      <h2 className="mt-10 font-serif-cn text-xl text-[#003645] font-semibold">{t("agentHub.commissions")}</h2>
      <div className="mt-3 rounded-lg border border-[#e5ebeb] bg-white overflow-hidden" data-testid="commissions-list">
        {commissions.length === 0 ? (
          <div className="p-6 text-[#4a6e77]">{t("common.empty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-[#4a6e77]">
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Type</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Order</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Buyer</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb] text-right">Amount</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Date</th>
            </tr></thead>
            <tbody>
              {commissions.map((c) => (
                <tr key={c.id}>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#003645]">{c.type}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77] text-xs">{c.order_no}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">{c.buyer_name}</td>
                  <td className={`py-3 px-4 border-b border-[#e5ebeb] text-right font-semibold ${c.amount >= 0 ? "text-[#00868e]" : "text-red-700"}`}>
                    {c.amount >= 0 ? "+" : ""}{formatRM(c.amount)}
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77] text-xs">{new Date(c.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Withdrawals */}
      <h2 className="mt-10 font-serif-cn text-xl text-[#003645] font-semibold">{t("agentHub.withdrawals")}</h2>
      <div className="mt-3 rounded-lg border border-[#e5ebeb] bg-white overflow-hidden" data-testid="withdrawals-list">
        {withdrawals.length === 0 ? (
          <div className="p-6 text-[#4a6e77]">{t("common.empty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-[#4a6e77]">
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Amount</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Status</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Note</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Date</th>
            </tr></thead>
            <tbody>
              {withdrawals.map((w) => (
                <tr key={w.id}>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] font-semibold text-[#005870]">{formatRM(w.amount)}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb]">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      w.status === "paid" ? "bg-[#00868e]/10 text-[#00868e]" :
                      w.status === "rejected" ? "bg-red-50 text-red-700" :
                      "bg-amber-50 text-amber-700"
                    }`}>
                      {t(`agentHub.statuses.${w.status}`)}
                    </span>
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">{w.admin_note || "-"}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77] text-xs">{new Date(w.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
